import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { plannerUiSlice } from 'src/api/planner-ui-slice';
import { useGetModalFlagsQuery, useUpdateModalFlagsMutation } from 'src/api/v2/user-v2-ApiSlice';
import { useDeleteFloorMutation } from 'src/api/v2/floor-v2-ApiSlice';
import { useDeleteTectlyFloorMutation, useGetTectlyMonthlyLimitQuery } from 'src/api/v2/tectly-v2-ApiSlice';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';
import { floorAccordionDomId } from 'src/pages/PlannerPage/FloorView/FloorAccordionView';
import { RecognitionContext, RecognitionOutcome } from 'src/pages/PlannerPage/RoomRecognition/recognitionTypes';
import { useRoomRecognition } from 'src/pages/PlannerPage/RoomRecognition/useRoomRecognition';
import { RoomRecognitionProgressModal } from 'src/pages/PlannerPage/RoomRecognition/RoomRecognitionProgressModal';
import { MeasurementDetectedModal } from 'src/pages/PlannerPage/RoomRecognition/MeasurementDetectedModal';
import { NoMeasurementFoundModal } from 'src/pages/PlannerPage/RoomRecognition/NoMeasurementFoundModal';
import { RoomDetectionUnsuccessfulModal } from 'src/pages/PlannerPage/RoomRecognition/RoomDetectionUnsuccessfulModal';
import { RoomDetectedModal } from 'src/pages/PlannerPage/RoomRecognition/RoomDetectedModal';
import { RoomRecognitionLimitReachedModal } from 'src/pages/PlannerPage/RoomRecognition/RoomRecognitionLimitReachedModal';

const ANCHOR_POLL_INTERVAL_MS = 200;
const MAX_ANCHOR_POLL_ATTEMPTS = 75;

interface RoomRecognitionFlowProps {
    context: RecognitionContext;
    onFinished: () => void;
    onUploadNewLayout: () => void;
    onShowFloorList: () => void;
    onStartAutoPlan: () => void;
}

export function RoomRecognitionFlow({ context, onFinished, onUploadNewLayout, onShowFloorList, onStartAutoPlan }: RoomRecognitionFlowProps) {
    const dispatch = useAppDispatch();
    const { checklist, isProcessing, result, start, cancel, clearResult } = useRoomRecognition();
    const { data: modalFlags } = useGetModalFlagsQuery();
    const [updateModalFlags] = useUpdateModalFlagsMutation();
    const [deleteFloor] = useDeleteFloorMutation();
    const [deleteTectlyFloor] = useDeleteTectlyFloorMutation();
    const { data: monthlyLimit = 0 } = useGetTectlyMonthlyLimitQuery();

    const [awaitingScale, setAwaitingScale] = useState(false);
    const [showRoomDetected, setShowRoomDetected] = useState(false);
    const [roomDetectedAnchor, setRoomDetectedAnchor] = useState<HTMLElement | null>(null);

    const scaleSetupCompletedFloorId = useAppSelector((state) => state.plannerUi.scaleSetupCompletedFloorId);
    const roomDetectedModalShown = useAppSelector((state) => state.plannerUi.roomDetectedModalShown);
    const selectedFloorId = useAppSelector((state) => state.plannerUi.selectedFloorId);

    const onFinishedRef = useRef(onFinished);
    useEffect(() => {
        onFinishedRef.current = onFinished;
    }, [onFinished]);

    const startedRef = useRef(false);
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        start(context);
    }, [start, context]);

    useEffect(() => () => cancel(), [cancel]);

    // Preload the planner canvas the moment recognition finishes
    useEffect(() => {
        if (result == null) return;
        dispatch(plannerUiSlice.actions.setSelectedFloorId(context.floorId));
    }, [result, context.floorId, dispatch]);

    // Only after the user finishes setting the scale do we reveal the floor list
    // and the room-detected tooltip, matching the legacy planner sequencing.
    useEffect(() => {
        if (!awaitingScale || scaleSetupCompletedFloorId !== context.floorId) return;
        setAwaitingScale(false);
        dispatch(plannerUiSlice.actions.setScaleSetupCompletedFloorId(null));
        onShowFloorList();
        setShowRoomDetected(true);
    }, [awaitingScale, scaleSetupCompletedFloorId, context.floorId, dispatch, onShowFloorList]);

    // The floor accordion only exists once the floor list tab has rendered the
    // freshly created floor, which lands after the results refetch. Poll for it.
    useEffect(() => {
        if (!showRoomDetected || roomDetectedAnchor != null) return;
        let attempts = 0;
        const interval = setInterval(() => {
            attempts++;
            const anchor = document.getElementById(floorAccordionDomId(context.floorId));
            if (anchor != null) {
                anchor.scrollIntoView({ block: 'center' });
                setRoomDetectedAnchor(anchor);
                clearInterval(interval);
            } else if (attempts >= MAX_ANCHOR_POLL_ATTEMPTS) {
                clearInterval(interval);
                setShowRoomDetected(false);
                onFinishedRef.current();
            }
        }, ANCHOR_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [showRoomDetected, roomDetectedAnchor, context.floorId]);

    const startScaleSetup = useCallback(() => {
        dispatch(plannerUiSlice.actions.setPendingScaleSetupFloorId(context.floorId));
    }, [dispatch, context.floorId]);

    const handleContinue = useCallback(() => {
        const roomsDetected = result?.roomsDetected ?? false;
        startScaleSetup();
        clearResult();

        const shouldShowRoomDetected =
            roomsDetected &&
            modalFlags?.show_room_detected_modal !== false &&
            !roomDetectedModalShown;

        if (!shouldShowRoomDetected) {
            onFinished();
            return;
        }

        // The tooltip is deferred until the scale-saved signal arrives (see effect above).
        dispatch(plannerUiSlice.actions.setRoomDetectedModalShown(true));
        dispatch(plannerUiSlice.actions.setScaleSetupCompletedFloorId(null));
        setAwaitingScale(true);
    }, [result, startScaleSetup, clearResult, modalFlags, roomDetectedModalShown, onFinished, dispatch]);

    const handleUploadNewLayout = useCallback(async () => {
        if (selectedFloorId === context.floorId) {
            dispatch(plannerUiSlice.actions.setSelectedFloorId(null));
        }
        // Tectly-side delete is best-effort; the uPlan floor delete is what must
        // succeed before we discard it and reopen the wizard for a fresh layout.
        await deleteTectlyFloor(context.floorId).unwrap().catch(() => undefined);
        try {
            await deleteFloor(context.floorId).unwrap();
        } catch {
            dispatch(showSnackBar({ text: ts('delete_floor_error', 'Failed to delete floor'), severity: 'error' }));
            return;
        }
        clearResult();
        onUploadNewLayout();
    }, [clearResult, deleteTectlyFloor, deleteFloor, context.floorId, onUploadNewLayout, dispatch, selectedFloorId]);

    const handleGotIt = useCallback((dontShowAgain: boolean) => {
        if (dontShowAgain) {
            updateModalFlags({ show_room_detected_modal: false }).unwrap().catch(() => undefined);
        }
        setShowRoomDetected(false);
        onStartAutoPlan();
        onFinished();
    }, [updateModalFlags, onFinished, onStartAutoPlan]);

    if (isProcessing) {
        return <RoomRecognitionProgressModal checklist={checklist} monthlyLimit={monthlyLimit} />;
    }

    if (result) {
        switch (result.outcome) {
            case RecognitionOutcome.MEASUREMENT_DETECTED:
                return <MeasurementDetectedModal onContinue={handleContinue} />;
            case RecognitionOutcome.NO_MEASUREMENT:
                return <NoMeasurementFoundModal onContinue={handleContinue} />;
            case RecognitionOutcome.UNSUCCESSFUL:
                return (
                    <RoomDetectionUnsuccessfulModal
                        onContinue={handleContinue}
                        onUploadNewLayout={handleUploadNewLayout}
                    />
                );
            case RecognitionOutcome.LIMIT_EXCEEDED:
                return (
                    <RoomRecognitionLimitReachedModal
                        monthlyLimit={monthlyLimit}
                        onContinue={handleContinue}
                    />
                );
        }
    }

    if (showRoomDetected && roomDetectedAnchor != null) {
        return <RoomDetectedModal anchorEl={roomDetectedAnchor} onGotIt={handleGotIt} />;
    }

    return null;
}